import React, { useState, useEffect, useRef } from 'react';
import './AdminDashboard.css';
import workersImage from './workers.png';
import adminOnlyImage from '../../admin-panel.png';
import AdminSound from '../../../src/AdminSound.mp3';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const currentUserEmail = localStorage.getItem('userEmail');
  const adminEmail = 'seifeddinejamai8@gmail.com'; // Updated to match AdminAccessGate admin email

  const isAdmin = currentUserEmail === adminEmail;

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchAnalytics();
      // Play audio when admin dashboard loads
      if (audioRef.current) {
        audioRef.current.volume = volume;
        audioRef.current.play().catch(err => console.log('Audio play failed:', err));
      }
    }
  }, [isAdmin, volume]);

  const fetchUsers = () => {
    fetch('http://localhost:5000/api/users')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setUsers(data);
        } else {
          console.error('Fetched users data is not an array:', data);
          setUsers([]);
        }
      })
      .catch((error) => console.error('Error fetching users:', error));
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:5000/api/admin/dashboard');
      const data = await response.json();

      if (data.success) {
        setAnalytics(data.dashboard);
      } else {
        console.error('Failed to load analytics data');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearAllData = async () => {
    if (window.confirm('Are you sure you want to clear all user data? This action cannot be undone.')) {
      try {
        const response = await fetch('http://localhost:5000/api/admin/clear-data', {
          method: 'POST'
        });
        const data = await response.json();

        if (data.success) {
          alert('All data cleared successfully');
          fetchAnalytics(); // Refresh data
        } else {
          alert('Failed to clear data');
        }
      } catch (err) {
        alert('Error clearing data: ' + err.message);
      }
    }
  };

  const exportData = () => {
    window.open('http://localhost:5000/api/admin/export-data', '_blank');
  };

  const generateAIReport = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:5000/api/ai/generate-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analytics: analytics,
          users: users,
          userEmail: currentUserEmail
        })
      });
      const data = await response.json();

      if (data.success) {
        alert('AI Report Generated Successfully!\n\n' + data.report);
      } else {
        alert('Failed to generate AI report: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error generating AI report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : newVolume;
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.volume = !isMuted ? 0 : volume;
    }
  };

  if (!isAdmin) {
    return (
      <div id='AdminDashboard' className="admin-access-blocked">
        <img src={adminOnlyImage} alt="Admin Only" className="admin-img" />
        <h2>This section is for administrators only.</h2>
        <p>If you believe this is a mistake, please contact support.</p>
      </div>
    );
  }

  // Prepare real analytics data for charts
  const interactionData = analytics ? [
    { name: 'Clicks', value: analytics.totalClicks || 0, color: '#4ea8de' },
    { name: 'Inputs', value: analytics.totalInputs || 0, color: '#82ca9d' },
    { name: 'Contacts', value: analytics.totalContacts || 0, color: '#ffc658' },
  ] : [];

  const mostClickedData = analytics ? Object.entries(analytics.mostClickedElements || {}).slice(0, 5).map(([element, count]) => ({
    element: element.length > 20 ? element.substring(0, 20) + '...' : element,
    count
  })) : [];

  const mostActivePagesData = analytics ? Object.entries(analytics.mostActivePages || {}).slice(0, 5).map(([page, count]) => ({
    page: page.split('/').pop() || 'Home',
    count
  })) : [];

  // Real data for charts from analytics (fallback to mocks if not available)
  const newUserData = analytics?.newUsersTrend ? analytics.newUsersTrend.map(item => ({
    date: item.date,
    'New Users (%)': item.growthRate,
    'Total Users': item.totalUsers
  })) : [
    { date: 'Jan 1', 'New Users (%)': 2.5, 'Total Users': users.length },
    { date: 'Jan 8', 'New Users (%)': 3.1, 'Total Users': users.length },
    { date: 'Jan 15', 'New Users (%)': 2.8, 'Total Users': users.length },
    { date: 'Jan 22', 'New Users (%)': 3.5, 'Total Users': users.length },
    { date: 'Jan 29', 'New Users (%)': 4.0, 'Total Users': users.length },
  ];

  const conversionRateData = analytics?.conversionRate ? [
    { name: 'Subscribed Users', value: analytics.conversionRate.subscribed },
    { name: 'Non-Subscribed Users', value: analytics.conversionRate.nonSubscribed },
  ] : [
    { name: 'Subscribed Users', value: users.filter(u => u.planStatus !== 'free').length },
    { name: 'Non-Subscribed Users', value: users.filter(u => u.planStatus === 'free').length },
  ];
  const CONVERSION_COLORS = ['#4ea8de', '#666666'];

  const userTypeData = analytics?.userTypeDistribution ? [
    { name: 'Students', value: analytics.userTypeDistribution.students },
    { name: 'Freelancers', value: analytics.userTypeDistribution.freelancers },
    { name: 'Enterprise', value: analytics.userTypeDistribution.enterprise },
  ] : [
    { name: 'Students', value: users.filter(u => u.role === 'student').length },
    { name: 'Freelancers', value: users.filter(u => u.role === 'freelancer').length },
    { name: 'Enterprise', value: users.filter(u => u.role === 'enterprise').length },
  ];
  const USER_TYPE_COLORS = ['#8884d8', '#82ca9d', '#ffc658'];

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <h2>Admin Panel</h2>
        <ul>
          <li className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>📊 Dashboard</li>
          <li className={activeTab === 'analytics' ? 'active' : ''} onClick={() => setActiveTab('analytics')}>📈 Analytics</li>
          <li className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>👥 Users</li>
          <li className={activeTab === 'subscriptions' ? 'active' : ''} onClick={() => setActiveTab('subscriptions')}>💳 Subscriptions</li>
          <li className={activeTab === 'plans' ? 'active' : ''} onClick={() => setActiveTab('plans')}>📦 Plans</li>
          <li className={activeTab === 'workers' ? 'active' : ''} onClick={() => setActiveTab('workers')}><img src={workersImage} alt="Workers" className="worker-icon" /> Workers</li>
        </ul>
        <div className="sidebar-actions">
          <button onClick={exportData} className="sidebar-btn export">📊 Export Data</button>
          <button onClick={clearAllData} className="sidebar-btn danger">🗑️ Clear Data</button>
          <button onClick={fetchAnalytics} className="sidebar-btn">🔄 Refresh</button>
          <button onClick={generateAIReport} className="sidebar-btn rm-report" disabled={loading || !analytics || users.length === 0}> 🤖 RapidMind Smart AI Report</button>
          <button onClick={() => { localStorage.clear(); window.location.href = '/'; }} className="sidebar-btn logout">🚪 Logout</button>
        </div>
      </aside>

      <main className="admin-main">
        <h1>Welcome Administrator!</h1>
        {activeTab === 'overview' && (
          <>
            <div className="cards">
              <div className="card"><h2>Total Users</h2><p>{users.length || 0}</p></div>
              <div className="card"><h2>New Today</h2><p>{analytics?.newUsersToday || 0}</p></div>
              <div className="card"><h2>Active Subscriptions</h2><p>{users.filter(u => u.planStatus !== 'free').length || 0}</p></div>
              <div className="card"><h2>Growth %</h2><p>{analytics?.growthRate ? `${analytics.growthRate}%` : '+0%'}</p></div>
            </div>

            <div className="chart-container">
              <h2>New User Growth (%)</h2>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={newUserData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3f47" />
                  <XAxis dataKey="date" stroke="white" />
                  <YAxis stroke="white" tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={{ backgroundColor: '#282c34', border: '1px solid #4ea8de', color: 'white' }} labelStyle={{ color: '#4ea8de' }} />
                  <Legend wrapperStyle={{ color: 'white' }} />
                  <Line type="monotone" dataKey="New Users (%)" stroke="#4ea8de" strokeWidth={2} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="Total Users" stroke="#ffc658" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="pie-charts-section">
              <div className="chart-container pie-chart-item">
                <h2>User Conversion Rate</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={conversionRateData} cx="50%" cy="50%" outerRadius={100} labelLine={false} fill="#8884d8" dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                      {conversionRateData.map((entry, i) => <Cell key={`cell-${i}`} fill={CONVERSION_COLORS[i % CONVERSION_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} users`, n]} contentStyle={{ backgroundColor: '#282c34', border: '1px solid #4ea8de', color: 'white' }} labelStyle={{ color: '#4ea8de' }} />
                    <Legend wrapperStyle={{ color: 'white' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container pie-chart-item">
                <h2>User Type Distribution (Subscribed)</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={userTypeData} cx="50%" cy="50%" outerRadius={100} labelLine={false} fill="#8884d8" dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                      {userTypeData.map((entry, i) => <Cell key={`cell-${i}`} fill={USER_TYPE_COLORS[i % USER_TYPE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} users`, n]} contentStyle={{ backgroundColor: '#282c34', border: '1px solid #4ea8de', color: 'white' }} labelStyle={{ color: '#4ea8de' }} />
                    <Legend wrapperStyle={{ color: 'white' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {activeTab === 'analytics' && analytics && (
          <>
            <div className="chart-container">
              <h2>User Interactions</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={interactionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3f47" />
                  <XAxis dataKey="name" stroke="white" />
                  <YAxis stroke="white" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#4ea8de" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h2>Most Clicked Elements</h2>
              <ul>
                {mostClickedData.map((item, index) => (
                  <li key={index}>{item.element}: {item.count} clicks</li>
                ))}
              </ul>
            </div>

            <div className="chart-container">
              <h2>Most Active Pages</h2>
              <ul>
                {mostActivePagesData.map((item, index) => (
                  <li key={index}>{item.page}: {item.count} visits</li>
                ))}
              </ul>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <div className="users-list">
            <h2>Fetched Users ({users.length || 0})</h2>
            {users.length === 0 ? (
              <p>No users found.</p>
            ) : (
              <ul>
                {users.map((user) => (
                  <li key={user.uid}>
                    {user.displayName || user.email} ({user.email}) - Role: {user.role || 'unknown'} - Plan: {user.planStatus || 'free'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="tab-placeholder">
            <h2>Subscriptions Management</h2>
            <p>Active Subscriptions: {users.filter(u => u.planStatus !== 'free').length || 0}</p>
            <ul>
              {users.filter(u => u.planStatus !== 'free').map((user) => (
                <li key={user.uid}>{user.displayName || user.email} - {user.planStatus}</li>
              ))}
            </ul>
            <p>Coming soon: Edit/cancel subscriptions.</p>
          </div>
        )}

        {activeTab === 'plans' && (
          <div className="tab-placeholder">
            <h2>Plans Management</h2>
            <p>Available Plans: Free, Basic, Pro, Enterprise</p>
            <p>Coming soon: Create/edit pricing plans.</p>
          </div>
        )}

        {activeTab === 'workers' && (
          <div className="tab-placeholder">
            <h2>Workers Management</h2>
            <p>Coming soon: Manage background workers and tasks.</p>
          </div>
        )}

        {loading && <div className="loading">Loading data...</div>}
        {analytics === null && !loading && <div className="error">Failed to load analytics. Click Refresh.</div>}

        <div className="audio-controls">
          <button onClick={toggleMute} className="mute-btn">
            {isMuted ? '🔊 Unmute' : '🔇 Mute'}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>
      </main>

      <audio ref={audioRef} src={AdminSound} loop />
    </div>
  );
}

export default AdminDashboard;
